'use client';

import { useState, useRef, useEffect, useLayoutEffect, useMemo, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs, { type Dayjs } from 'dayjs';
import styles from '../styles/DateTimePicker.module.css';

const MINUTE_STEP = 30;

const pickerTheme = createTheme({
  palette: {
    primary: { main: '#4F46E5', contrastText: '#ffffff' },
    text: {
      primary: '#111827',
      secondary: '#6b7280',
    },
  },
});

interface DateTimePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  compact?: boolean;
}

function roundMinuteToStep(minute: number): number {
  return Math.round(minute / MINUTE_STEP) * MINUTE_STEP;
}

function roundMinuteUpToStep(minute: number): number {
  if (minute % MINUTE_STEP === 0) return minute;
  return Math.ceil(minute / MINUTE_STEP) * MINUTE_STEP;
}

export default function DateTimePicker({
  selected,
  onChange,
  minDate,
  maxDate,
  compact = false,
}: DateTimePickerProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [mode, setMode] = useState<'date' | 'time'>('date');
  const [selectedDate, setSelectedDate] = useState<Date | null>(selected);
  const [selectedTime, setSelectedTime] = useState<{ hour: number; minute: number } | null>(
    selected ? { hour: selected.getHours(), minute: roundMinuteToStep(selected.getMinutes()) } : null,
  );
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});
  const [mobileSheet, setMobileSheet] = useState(false);
  const [viewport, setViewport] = useState({ w: 1024, h: 768 });
  const containerRef = useRef<HTMLDivElement>(null);

  const minDayjs = dayjs(minDate ?? new Date()).startOf('day');
  const maxDayjs = maxDate ? dayjs(maxDate).endOf('day') : undefined;
  const minDateTimeDayjs = useMemo(() => dayjs(minDate ?? new Date()), [minDate]);
  const maxDateTimeDayjs = useMemo(() => (maxDate ? dayjs(maxDate) : null), [maxDate]);

  const ceilToStep = (value: Dayjs): Dayjs => {
    let roundedMinute = roundMinuteUpToStep(value.minute());
    let result = value.second(0).millisecond(0);
    if (roundedMinute >= 60) {
      roundedMinute = 0;
      result = result.add(1, 'hour');
    }
    return result.minute(roundedMinute);
  };

  useEffect(() => {
    if (selected) {
      setSelectedDate(selected);
      setSelectedTime({ hour: selected.getHours(), minute: roundMinuteToStep(selected.getMinutes()) });
    }
  }, [selected]);

  useLayoutEffect(() => {
    setViewport({ w: window.innerWidth, h: window.innerHeight });
  }, []);

  useLayoutEffect(() => {
    if (!showPicker || !containerRef.current) return;

    const update = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      setViewport({ w: vw, h: vh });
      const isMobile = vw <= 768;
      const viewportMargin = isMobile ? 8 : 12;

      if (isMobile) {
        const mobileMaxHeight = Math.max(260, vh - viewportMargin * 2);
        setMobileSheet(true);
        setPopoverStyle({
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          top: 'auto',
          width: '100%',
          maxWidth: '100vw',
          maxHeight: mobileMaxHeight,
          boxSizing: 'border-box',
          zIndex: 20000,
          borderRadius: '1rem 1rem 0 0',
        });
        return;
      }

      setMobileSheet(false);
      const panelWidth = Math.min(compact ? 300 : 320, vw - viewportMargin * 2);
      let left = rect.left;
      if (left + panelWidth > vw - viewportMargin) {
        left = Math.max(viewportMargin, vw - viewportMargin - panelWidth);
      } else {
        left = Math.max(viewportMargin, left);
      }

      const gap = 8;
      const spaceBelow = Math.max(160, vh - rect.bottom - gap - viewportMargin);
      const spaceAbove = Math.max(160, rect.top - gap - viewportMargin);
      const openUp = spaceAbove > spaceBelow;
      const maxHeight = Math.max(260, Math.min(openUp ? spaceAbove : spaceBelow, vh - viewportMargin * 2));

      if (openUp) {
        setPopoverStyle({
          position: 'fixed',
          left,
          width: panelWidth,
          maxWidth: `min(100vw - ${viewportMargin * 2}px, ${panelWidth}px)`,
          maxHeight,
          bottom: vh - rect.top + gap,
          top: 'auto',
          boxSizing: 'border-box',
          zIndex: 20000,
        });
      } else {
        setPopoverStyle({
          position: 'fixed',
          left,
          width: panelWidth,
          maxWidth: `min(100vw - ${viewportMargin * 2}px, ${panelWidth}px)`,
          maxHeight,
          top: rect.bottom + gap,
          bottom: 'auto',
          boxSizing: 'border-box',
          zIndex: 20000,
        });
      }
    };

    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [showPicker, mode, compact]);

  const formatDisplayValue = () => {
    if (!selected) return 'Select date and time';
    const date = selected.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const time = selected.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${date} at ${time}`;
  };

  const handleCalendarChange = (value: Dayjs | null) => {
    setSelectedDate(value ? value.toDate() : null);
  };

  const handleDateConfirm = () => {
    if (!selectedDate) return;
    const sd = dayjs(selectedDate);
    if (sd.startOf('day').isBefore(minDayjs)) return;
    if (maxDayjs && sd.endOf('day').isAfter(maxDayjs)) return;
    setMode('time');
    const d = dayjs(selectedDate);
    const isToday = d.isSame(dayjs(), 'day');
    const now = dayjs();
    let initialTime = selectedTime
      ? { hour: selectedTime.hour, minute: selectedTime.minute }
      : { hour: now.hour(), minute: roundMinuteUpToStep(now.minute()) };
    if (initialTime.minute >= 60) {
      initialTime = { hour: initialTime.hour + 1, minute: initialTime.minute - 60 };
    }
    if (initialTime.hour >= 24) initialTime = { hour: 23, minute: 55 };

    if (isToday) {
      let h = now.hour();
      let m = roundMinuteUpToStep(now.minute());
      if (m >= 60) {
        m = 0;
        h += 1;
      }
      if (h >= 24) h = 23;
      const minT = { hour: h, minute: m };
      if (
        initialTime.hour < minT.hour ||
        (initialTime.hour === minT.hour && initialTime.minute < minT.minute)
      ) {
        initialTime = minT;
      }
    } else if (!selectedTime) {
      initialTime = { hour: 9, minute: 0 };
    }

    setSelectedTime(initialTime);
    const dateWithTime = new Date(selectedDate);
    dateWithTime.setHours(initialTime.hour, initialTime.minute, 0, 0);
    onChange(dateWithTime);
  };

  const handleTimeSlotSelect = (slot: Dayjs) => {
    if (!selectedDate) return;
    setSelectedTime({ hour: slot.hour(), minute: slot.minute() });
    onChange(slot.toDate());
  };

  const handleBackToDate = () => {
    setMode('date');
  };

  const handleConfirm = () => {
    if (selectedDate && selectedTime) {
      const finalDate = new Date(selectedDate);
      finalDate.setHours(selectedTime.hour, selectedTime.minute, 0, 0);
      const finalDayjs = dayjs(finalDate);
      if (finalDayjs.isBefore(minDateTimeDayjs)) return;
      if (maxDate && finalDayjs.isAfter(dayjs(maxDate))) return;
      onChange(finalDate);
      setShowPicker(false);
      setMode('date');
    }
  };

  const handleCancel = () => {
    setShowPicker(false);
    setMode('date');
    if (selected) {
      setSelectedDate(selected);
      setSelectedTime({ hour: selected.getHours(), minute: roundMinuteToStep(selected.getMinutes()) });
    } else {
      setSelectedDate(null);
      setSelectedTime(null);
    }
  };

  const calendarValue = selectedDate ? dayjs(selectedDate) : null;
  const selectedDateDayjs = useMemo(() => (selectedDate ? dayjs(selectedDate) : null), [selectedDate]);
  const isSelectedDateOutsideRange = useMemo(() => {
    if (!selectedDateDayjs) return false;
    if (selectedDateDayjs.startOf('day').isBefore(minDayjs)) return true;
    if (maxDayjs && selectedDateDayjs.endOf('day').isAfter(maxDayjs)) return true;
    return false;
  }, [selectedDateDayjs, minDayjs, maxDayjs]);

  const isSelectedDateTimeOutsideRange = useMemo(() => {
    if (!selectedDateDayjs || !selectedTime) return false;
    const dt = selectedDateDayjs
      .hour(selectedTime.hour)
      .minute(selectedTime.minute)
      .second(0)
      .millisecond(0);
    if (dt.isBefore(minDateTimeDayjs)) return true;
    if (maxDate && dt.isAfter(dayjs(maxDate))) return true;
    return false;
  }, [selectedDateDayjs, selectedTime, minDateTimeDayjs, maxDate]);

  const timeOptions = useMemo(() => {
    if (!selectedDateDayjs) return [] as Dayjs[];
    const dayStart = selectedDateDayjs.startOf('day');
    const dayEnd = selectedDateDayjs.endOf('day');
    const lowerBound = selectedDateDayjs.isSame(minDateTimeDayjs, 'day') ? minDateTimeDayjs : dayStart;
    const upperBound =
      maxDateTimeDayjs && selectedDateDayjs.isSame(maxDateTimeDayjs, 'day') ? maxDateTimeDayjs : dayEnd;

    let cursor = ceilToStep(lowerBound);
    if (cursor.isBefore(dayStart)) cursor = dayStart;

    const slots: Dayjs[] = [];
    while (cursor.isBefore(upperBound) || cursor.isSame(upperBound)) {
      slots.push(cursor);
      cursor = cursor.add(MINUTE_STEP, 'minute');
    }
    return slots;
  }, [selectedDateDayjs, minDateTimeDayjs, maxDateTimeDayjs]);

  /** MUI X uses PickerViewRoot 320×336 on DateCalendar/TimeClock — causes overflow + blank space in narrow panels. */
  const layout = useMemo(() => {
    const w = viewport.w;
    const tight = w < 400;
    const dayW = tight ? 26 : compact ? 27 : 28;
    const dayH = tight ? 24 : compact ? 25 : 26;
    const slideMin = Math.max(150, (dayH + 4) * 6 + 4);
    return { tight, dayW, dayH, slideMin };
  }, [viewport.w, compact]);

  const calendarSx = useMemo(
    () => ({
    // DateCalendar root = PickerViewRoot: kill fixed 320×336
    width: '100% !important',
    maxWidth: '100% !important',
    height: 'auto !important',
    maxHeight: 'none !important',
    minHeight: '0 !important',
    margin: '0 auto',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    overflowY: 'visible',
    mx: 'auto',
    p: compact ? 0.2 : 0.3,
    color: '#111827',
    '& .MuiDateCalendar-viewTransitionContainer': {
      flex: '0 0 auto',
      minHeight: 0,
      maxHeight: 'none',
      overflow: 'visible',
    },
    '& .MuiPickersCalendarHeader-root': {
      px: 0.125,
      py: 0,
      minHeight: 30,
      maxHeight: 30,
      marginBottom: 0,
      color: '#111827',
    },
    '& .MuiPickersCalendarHeader-label': {
      fontSize: '0.78rem',
      color: '#111827',
    },
    '& .MuiPickersArrowSwitcher-root .MuiIconButton-root': {
      color: '#374151',
      padding: 0.35,
    },
    '& .MuiYearCalendar-root': {
      color: '#111827',
      overflowY: 'auto !important',
      overflowX: 'hidden',
      maxHeight: '220px !important',
      height: '220px !important',
      width: '100% !important',
      maxWidth: '100% !important',
      rowGap: '6px',
      padding: '4px 0',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': {
        width: 0,
        height: 0,
        display: 'none',
      },
    },
    '& .MuiYearCalendar-button': {
      color: '#111827',
      height: 30,
      width: 60,
      fontSize: '0.75rem',
      borderRadius: 15,
    },
    '& .MuiYearCalendar-button.MuiYearCalendar-selected': {
      color: '#ffffff',
      backgroundColor: '#4F46E5',
      '&:hover, &:focus': {
        color: '#ffffff',
        backgroundColor: '#4338CA',
      },
    },
    '& .MuiMonthCalendar-root': {
      color: '#111827',
      width: '100% !important',
      maxWidth: '100% !important',
      boxSizing: 'border-box',
    },
    '& .MuiDayCalendar-root': {
      width: '100% !important',
      maxWidth: '100% !important',
      overflowX: 'hidden',
      boxSizing: 'border-box',
    },
    '& .MuiMonthCalendar-button': {
      color: '#111827',
    },
    '& .MuiMonthCalendar-button.MuiMonthCalendar-selected': {
      color: '#ffffff',
      backgroundColor: '#4F46E5',
      '&:hover, &:focus': {
        color: '#ffffff',
        backgroundColor: '#4338CA',
      },
    },
    '& .MuiDayCalendar-weekContainer': { marginTop: '1px' },
    '& .MuiDayCalendar-weekDayLabel': {
      width: layout.dayW,
      fontSize: '0.62rem',
      color: '#6b7280',
    },
    '& .MuiPickersDay-root': {
      width: layout.dayW,
      height: layout.dayH,
      fontSize: layout.tight ? '0.65rem' : '0.7rem',
      color: '#374151',
    },
    '& .MuiPickersDay-root.MuiPickersDay-selected': {
      color: '#ffffff',
      backgroundColor: '#4F46E5',
      '&:hover, &:focus': {
        color: '#ffffff',
        backgroundColor: '#4338CA',
      },
    },
    '& .MuiDayCalendar-slideTransition': { minHeight: layout.slideMin },
    '& .MuiDayCalendar-monthContainer': { paddingBottom: 0 },
    }),
    [layout, compact],
  );

  const popover = showPicker && (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider theme={pickerTheme}>
        <div
          className={`${styles.dropdown} ${mobileSheet ? styles.dropdownFixed : ''}`}
          style={popoverStyle}
          role="dialog"
          aria-modal="true"
          aria-label="Choose date and time"
        >
          <div className={styles.dropdownHeader}>
            <h3 className={styles.dropdownTitle}>
              {mode === 'date' ? (
                <>
                  Select date:{' '}
                  {selectedDate
                    ? selectedDate.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : '--'}
                </>
              ) : (
                <>
                  Select time:{' '}
                  {selectedTime
                    ? `${selectedTime.hour.toString().padStart(2, '0')}:${selectedTime.minute.toString().padStart(2, '0')}`
                    : '--:--'}
                </>
              )}
            </h3>
            {mode === 'time' && (
              <button type="button" className={styles.changeDateButton} onClick={handleBackToDate}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M10 12L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Change Date
              </button>
            )}
            <button type="button" className={styles.closeButton} onClick={handleCancel}>
              ×
            </button>
          </div>

          <div className={styles.pickerContent}>
            {mode === 'date' ? (
              <div className={`${styles.calendarSection} ${styles.muiCalendarWrap}`}>
                <DateCalendar
                  value={calendarValue}
                  onChange={handleCalendarChange}
                  minDate={minDayjs}
                  maxDate={maxDayjs}
                  reduceAnimations
                  yearsPerRow={4}
                  monthsPerRow={4}
                  sx={calendarSx}
                />
              </div>
            ) : (
              <div className={`${styles.timeSection} ${styles.clockSection}`}>
                {timeOptions.length > 0 ? (
                  <div className={styles.timeSlots} role="listbox" aria-label="Choose time slot">
                    {timeOptions.map((slot) => {
                      const isActive =
                        !!selectedTime &&
                        selectedTime.hour === slot.hour() &&
                        selectedTime.minute === slot.minute();
                      return (
                        <button
                          key={slot.format('YYYY-MM-DDTHH:mm')}
                          type="button"
                          className={`${styles.timeSlot} ${isActive ? styles.timeSlotActive : ''}`}
                          onClick={() => handleTimeSlotSelect(slot)}
                          aria-selected={isActive}
                        >
                          {slot.format('HH:mm')}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.noTimeSlots}>No valid time slots available for this date.</div>
                )}
              </div>
            )}
          </div>

          <div className={styles.dropdownFooter}>
            <button type="button" className={styles.cancelButton} onClick={handleCancel}>
              Cancel
            </button>
            {mode === 'date' ? (
              <button
                type="button"
                className={styles.confirmButton}
                onClick={handleDateConfirm}
                disabled={!selectedDate || isSelectedDateOutsideRange}
              >
                Continue to Time
              </button>
            ) : (
              <button
                type="button"
                className={styles.confirmButton}
                onClick={handleConfirm}
                disabled={!selectedDate || !selectedTime || isSelectedDateTimeOutsideRange}
              >
                Confirm
              </button>
            )}
          </div>
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  );

  return (
    <div
      className={`${styles.container} ${compact ? styles.containerCompact : ''}`}
      ref={containerRef}
    >
      <button type="button" className={styles.trigger} onClick={() => setShowPicker(!showPicker)}>
        <span className={styles.triggerText}>{formatDisplayValue()}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={styles.icon}
        >
          <path
            d="M6 2V4M14 2V4M3 8H17M5 4H15C16.1046 4 17 4.89543 17 6V16C17 17.1046 16.1046 18 15 18H5C3.89543 18 3 17.1046 3 16V6C3 4.89543 3.89543 4 5 4Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {typeof document !== 'undefined' && popover ? createPortal(popover, document.body) : null}
    </div>
  );
}
